import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheckCircle, FaTimes } from 'react-icons/fa';

/**
 * ThankYouModal - Shows after successful form submissions
 * Includes Google Ads conversion tracking
 */
const ThankYouModal = ({ 
  isOpen, 
  onClose, 
  title = "Thank You!", 
  message = "We'll get back to you within 24 hours.",
  autoClose = true,
  autoCloseDelay = 5000 
}) => {
  
  // Fire Google Ads conversion event when modal opens
  useEffect(() => {
    if (isOpen && typeof window !== 'undefined' && typeof window.gtag === 'function') {
      // Google Ads conversion tracking
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17797421129/LrUYCIi_tdMbEMmwvKZC'
      });
      console.log('✅ Google Ads conversion event fired');
    }
  }, [isOpen]);

  // Auto-close after delay
  useEffect(() => {
    if (isOpen && autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, autoCloseDelay, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-0 flex items-center justify-center z-[9999] p-4"
          >
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative overflow-hidden">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
              
              {/* Success animation background */}
              <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-emerald-50 opacity-50" />
              
              {/* Content */}
              <div className="relative text-center">
                {/* Animated checkmark */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ 
                    type: "spring", 
                    damping: 15, 
                    stiffness: 200,
                    delay: 0.1 
                  }}
                  className="mb-6"
                >
                  <div className="w-20 h-20 mx-auto bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                    <FaCheckCircle className="text-white text-4xl" />
                  </div>
                </motion.div>
                
                {/* Title */}
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-2xl font-bold text-gray-900 mb-3"
                >
                  {title}
                </motion.h3>
                
                {/* Message */}
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-gray-600 mb-6"
                >
                  {message}
                </motion.p>
                
                {/* Contact info */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-gray-50 rounded-xl p-4 mb-6"
                >
                  <p className="text-sm text-gray-500 mb-2">Need immediate assistance?</p>
                  <a 
                    href="tel:+1" 
                    className="text-lg font-semibold text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    
                  </a>
                </motion.div>
                
                {/* Close button */}
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  onClick={onClose}
                  className="w-full py-3 px-6 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-300 transform hover:scale-[1.02]"
                >
                  Continue Browsing
                </motion.button>
                
                {/* Auto-close indicator */}
                {autoClose && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="text-xs text-gray-400 mt-4"
                  >
                    This window will close automatically
                  </motion.p>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ThankYouModal;
